import { hasStoredAnalyticsConsent } from 'utils/cookieConsent';
import { isGtmEnabled } from 'utils/tracking';
import { roundMoney } from 'utils/orderTotals';
import {
  ensureGtagStub,
  queueOrRunGtagEvent,
  whenAnalyticsReady,
} from 'utils/gtagLoader';
import { shouldShowDebugLogs } from 'utils/isDevelopment';
import type { CartItem, OrderDetail, Product } from 'utils/types';

export type GaItem = {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price: number;
  quantity: number;
};

/** GA4 purchase item shape (ex-VAT) for GTM dataLayer ecommerce.items */
export type PurchaseEcommerceItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  discount: number;
  coupon: string;
};

export type OrderSuccessAnalytics = {
  transaction_id: string;
  affiliation: string;
  value: number;
  tax: number;
  shipping: number;
  surcharge: number;
  currency: string;
  coupon: string;
  discount: number;
  items: PurchaseEcommerceItem[];
};

const PURCHASE_AFFILIATION = 'Abbey Dental Supplies';

export type OrderSuccessSession = {
  orderNumber: string;
  epChargeApplied?: boolean;
  analytics?: OrderSuccessAnalytics;
};

const PURCHASE_DEDUP_PREFIX = 'ga_purchase_sent_';
const SESSION_VIEW_CART_KEY = 'ga_session_view_cart';
const SESSION_BEGIN_CHECKOUT_KEY = 'ga_session_begin_checkout';

function lineValue(item: GaItem): number {
  return roundMoney(item.price * item.quantity);
}

export function cartItemsValue(items: GaItem[]): number {
  return roundMoney(items.reduce((sum, item) => sum + lineValue(item), 0));
}

function cartItemUnitGross(item: CartItem): number {
  if (item.priceIncVat != null && !Number.isNaN(item.priceIncVat)) {
    return item.priceIncVat;
  }
  const rate = item.taxRate != null ? item.taxRate / 100 : 0.2;
  return roundMoney(item.price * (1 + rate));
}

export function cartItemToGaItem(item: CartItem, quantity?: number): GaItem {
  const variantParts = [item.selectedMould, item.selectedShade].filter(
    (part) => part && part !== 'Select a mould' && part !== 'Select a shade',
  );
  const qty = quantity ?? item.quantity;
  return {
    item_id: item.productCode || item.productId,
    item_name: item.productName,
    item_category: item.category,
    item_variant: variantParts.length ? variantParts.join(' / ') : undefined,
    price: cartItemUnitGross(item),
    quantity: qty,
  };
}

export function productToGaItem(
  product: Product,
  quantity: number,
  variants?: { mould?: string; shade?: string },
): GaItem {
  const productAny = product as Product & {
    priceData?: { rspIncVat?: number };
  };
  const unitGross =
    productAny.priceData?.rspIncVat ??
    roundMoney(product.price * 1.2);
  const variantParts = [variants?.mould, variants?.shade].filter(
    (part) => part && part !== 'Select a mould' && part !== 'Select a shade',
  );
  return {
    item_id: product.code || product.id,
    item_name: product.name,
    item_category: product.category,
    item_variant: variantParts.length ? variantParts.join(' / ') : undefined,
    price: unitGross,
    quantity,
  };
}

function orderLineToPurchaseItem(
  line: OrderDetail['lineItems'][number],
): PurchaseEcommerceItem | null {
  if (!line.quantity || line.quantity <= 0) return null;
  return {
    item_id: line.variant?.code || String(line.variant?.id ?? line.id),
    item_name: line.description || line.variant?.description || 'Item',
    price: roundMoney(line.pricing.itemNet),
    quantity: line.quantity,
    discount: 0,
    coupon: '',
  };
}

function orderSurchargeExVat(order: OrderDetail): number {
  return roundMoney(
    (order.discountsSurcharges ?? [])
      .filter((entry) => entry.type === 'surcharge')
      .reduce((sum, entry) => sum + entry.net, 0),
  );
}

export function buildPurchaseAnalyticsFromOrder(
  order: OrderDetail,
): OrderSuccessAnalytics {
  const items = order.lineItems
    .map(orderLineToPurchaseItem)
    .filter((item): item is PurchaseEcommerceItem => item != null);

  return {
    transaction_id: order.orderNumber,
    affiliation: PURCHASE_AFFILIATION,
    value: roundMoney(order.totals.totalNet),
    tax: roundMoney(order.totals.totalVat),
    shipping: roundMoney(order.totals.deliveryNet),
    surcharge: orderSurchargeExVat(order),
    currency: order.currencyCode ?? 'GBP',
    coupon: '',
    discount: 0,
    items,
  };
}

export function buildPurchaseAnalytics(
  orderNumber: string,
  cartItems: CartItem[],
  options: {
    value: number;
    currency: string;
    tax: number;
    shipping: number;
    surcharge?: number;
  },
): OrderSuccessAnalytics {
  return {
    transaction_id: orderNumber,
    affiliation: PURCHASE_AFFILIATION,
    value: roundMoney(options.value),
    tax: roundMoney(options.tax),
    shipping: roundMoney(options.shipping),
    surcharge: roundMoney(options.surcharge ?? 0),
    currency: options.currency,
    coupon: '',
    discount: 0,
    items: cartItems.map((item) => ({
      item_id: item.productCode || item.productId,
      item_name: item.productName,
      price: roundMoney(item.price),
      quantity: item.quantity,
      discount: 0,
      coupon: '',
    })),
  };
}

export function parseOrderSuccessSession(raw: string | null): OrderSuccessSession | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OrderSuccessSession;
  } catch {
    return null;
  }
}

export function isPurchaseTracked(transactionId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return Boolean(sessionStorage.getItem(`${PURCHASE_DEDUP_PREFIX}${transactionId}`));
}

export function markPurchaseTracked(transactionId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(`${PURCHASE_DEDUP_PREFIX}${transactionId}`, String(Date.now()));
}

export function hasSessionFunnelEvent(key: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(key) === '1';
}

export function markSessionFunnelEvent(key: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(key, '1');
}

export const GA_SESSION_VIEW_CART_KEY = SESSION_VIEW_CART_KEY;
export const GA_SESSION_BEGIN_CHECKOUT_KEY = SESSION_BEGIN_CHECKOUT_KEY;

export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!isGtmEnabled() || !hasStoredAnalyticsConsent()) return;
  queueOrRunGtagEvent(eventName, params);
}

export function trackViewItem(item: GaItem, currency: string): void {
  trackEvent('view_item', {
    currency,
    value: lineValue(item),
    items: [item],
  });
}

export function trackAddToCart(item: GaItem, currency: string): void {
  trackEvent('add_to_cart', {
    currency,
    value: lineValue(item),
    items: [item],
  });
}

export function trackRemoveFromCart(item: GaItem, currency: string): void {
  trackEvent('remove_from_cart', {
    currency,
    value: lineValue(item),
    items: [item],
  });
}

export function trackViewCart(items: GaItem[], currency: string): void {
  if (items.length === 0 || hasSessionFunnelEvent(SESSION_VIEW_CART_KEY)) return;
  markSessionFunnelEvent(SESSION_VIEW_CART_KEY);
  trackEvent('view_cart', {
    currency,
    value: cartItemsValue(items),
    items,
  });
}

export function trackBeginCheckout(items: GaItem[], currency: string): void {
  if (items.length === 0 || hasSessionFunnelEvent(SESSION_BEGIN_CHECKOUT_KEY)) return;
  markSessionFunnelEvent(SESSION_BEGIN_CHECKOUT_KEY);
  trackEvent('begin_checkout', {
    currency,
    value: cartItemsValue(items),
    items,
  });
}

export function trackPurchase(payload: OrderSuccessAnalytics): void {
  if (typeof window === 'undefined') return;
  ensureGtagStub();
  
  const dataLayerEvent = {
    event: 'purchase',
    ecommerce: {
      transaction_id: payload.transaction_id,
      affiliation: payload.affiliation,
      value: payload.value,
      tax: payload.tax,
      shipping: payload.shipping,
      surcharge: payload.surcharge,
      currency: payload.currency,
      coupon: payload.coupon,
      discount: payload.discount,
      items: payload.items,
    },
  };
  
  // Verbose logging in development and staging
  const showLogs = shouldShowDebugLogs();
  if (showLogs) {
    console.log('[Analytics] Pushing purchase event to dataLayer:', dataLayerEvent);
    console.log('[Analytics] Current dataLayer before push:', window.dataLayer?.length || 0, 'events');
  }
  
  try {
    window.dataLayer!.push(dataLayerEvent);
    if (showLogs) {
      console.log('[Analytics] ✅ Successfully pushed to dataLayer');
      console.log('[Analytics] dataLayer now contains:', window.dataLayer?.length || 0, 'events');
      console.log('[Analytics] Last 3 dataLayer events:', window.dataLayer?.slice(-3));
    }
  } catch (error) {
    // Always log errors, even in production
    console.error('[Analytics] Failed to push to dataLayer:', error);
  }
  
  // Keep for debugging in console (doesn't log unless manually accessed)
  if (typeof window !== 'undefined') {
    (window as any).__lastPurchaseEvent = dataLayerEvent;
    if (showLogs) {
      console.log('[Analytics] Event saved to window.__lastPurchaseEvent for inspection');
    }
  }
}

/**
 * Sends purchase once per transaction (session-persisted dedup).
 * Waits for GA config; falls back to dataLayer queue via stub.
 */
export async function trackPurchaseOnce(
  payload: OrderSuccessAnalytics,
): Promise<boolean> {
  const showLogs = shouldShowDebugLogs();
  
  if (showLogs) {
    console.log('[Analytics] trackPurchaseOnce called', {
      transaction_id: payload.transaction_id,
      gtmEnabled: isGtmEnabled(),
      hasConsent: hasStoredAnalyticsConsent(),
      alreadyTracked: isPurchaseTracked(payload.transaction_id),
    });
  }

  if (!isGtmEnabled()) {
    if (showLogs) {
      console.log('[Analytics] GTM is disabled - skipping tracking');
    }
    return false;
  }
  
  if (!hasStoredAnalyticsConsent()) {
    if (showLogs) {
      console.log('[Analytics] Analytics consent not given - skipping tracking');
    }
    return false;
  }
  
  if (isPurchaseTracked(payload.transaction_id)) {
    if (showLogs) {
      console.log('[Analytics] Purchase already tracked for transaction:', payload.transaction_id);
    }
    return false;
  }

  ensureGtagStub();
  if (showLogs) {
    console.log('[Analytics] Waiting for analytics ready...');
  }
  await whenAnalyticsReady();
  if (showLogs) {
    console.log('[Analytics] Analytics ready, tracking purchase');
  }
  trackPurchase(payload);
  markPurchaseTracked(payload.transaction_id);
  if (showLogs) {
    console.log('[Analytics] Purchase tracked successfully');
  }
  return true;
}

/**
 * Manual test function for GTM purchase event.
 * Can be called from browser console for testing.
 * 
 * Usage:
 * import { testGtmPurchaseEvent } from 'utils/analytics';
 * testGtmPurchaseEvent();
 * 
 * Or add to window for console access:
 * window.testGtmPurchaseEvent = testGtmPurchaseEvent;
 */
export function testGtmPurchaseEvent(): void {
  console.log('🧪 Testing GTM Purchase Event');
  
  const testPayload: OrderSuccessAnalytics = {
    transaction_id: `TEST-${Date.now()}`,
    affiliation: 'Abbey Dental Supplies',
    value: 123.45,
    tax: 24.69,
    shipping: 9.95,
    surcharge: 0,
    currency: 'GBP',
    coupon: '',
    discount: 0,
    items: [
      {
        item_id: 'TEST-PRODUCT-001',
        item_name: 'Test Product',
        price: 50.00,
        quantity: 2,
        discount: 0,
        coupon: '',
      },
      {
        item_id: 'TEST-PRODUCT-002',
        item_name: 'Another Test Product',
        price: 23.45,
        quantity: 1,
        discount: 0,
        coupon: '',
      },
    ],
  };
  
  console.log('📦 Test payload:', testPayload);
  console.log('🔍 Environment check:', {
    gtmEnabled: isGtmEnabled(),
    hasConsent: hasStoredAnalyticsConsent(),
    hostname: window.location.hostname,
  });
  
  if (!isGtmEnabled()) {
    console.error('❌ GTM is disabled on this domain (staging?)');
    return;
  }
  
  if (!hasStoredAnalyticsConsent()) {
    console.warn('⚠️ Analytics consent not given - accept cookies first');
  }
  
  trackPurchase(testPayload);
  console.log('✅ Test purchase event sent to dataLayer');
  console.log('💡 Check window.dataLayer to verify');
  console.log('💡 Check window.__lastPurchaseEvent for the event data');
}
